import React from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import { lighten } from 'polished';
import Theme from '../common/NekoTheme';
import { ProOnly } from '../label/ProOnly';
import { Icon } from '@iconify/react';
import chevronDown from '@iconify/icons-mdi/chevron-down';
import { useOutsideClick, useComponentSize } from '../hooks';

const MenuButtonWrapper = Styled.div`
  display: inline-block;
`;

const MenuButton = Styled.button`
  align-items: center;
  background-color: ${Theme.blue};
  border: none;
  border-radius: 4px;
  box-sizing: border-box;
  color: white;
  display: inline-flex;
  justify-content: space-between;
  font-family: ${Theme.fontFamily};
  font-size: ${Theme.fontSizeText};
  height: 30px;
  min-width: 6em;
  padding: 0px 10px;
  text-align: center;
  transition: background-color .10s, opacity .25s;

  :not([disabled]):hover {
    cursor: pointer;
    background-color: ${lighten(0.1, Theme.blue)};
  }

  :disabled {
    opacity: 0.35;
  }

  :focus {
    outline: none;
  }

  & + button {
    margin-left: .25rem;
  }
`;

const MenuItemContainer = Styled.div`
  position: relative;
  display: block;
  margin-top: 18px;
  background-color: ${Theme.blue};
  position: absolute;
  z-index: 10;
  box-shadow: ${Theme.lightShadow};

  &:before {
    content: "";
    position: absolute;
    top: -18px;
    left: 50%;
    margin-left: -8px;
    border: 8px solid transparent;
    border-bottom: 10px solid ${Theme.blue};
  }
`;

const StyledMenuButton = props => {

  const { title, disabled = false, isPro = false, style = {} } = props;
  const [ isOpen, setIsOpen ] = React.useState(false);
  const wrapperRef = useOutsideClick(() => { setIsOpen(false) });
  const { ref: buttonRef, width: buttonWidth } = useComponentSize();

  const onClick = () => {
    setIsOpen(!isOpen);
  }

  const children = React.Children.map(props.children, child => {
    return React.cloneElement(child, {
      isPro,
      disabled: child.props.disabled || disabled,
      onClick: () => {
        if (child.props.onClick) {
          child.props.onClick();
        }
        onClick();
      }
    });
  });

  return (
    <MenuButtonWrapper ref={wrapperRef}>
      <MenuButton ref={buttonRef} type="button" disabled={disabled} onClick={onClick} style={style}>
        <span>{title}</span>
        <Icon icon={chevronDown} width="24" height="24" style={{ marginLeft: '4px' }} />
      </MenuButton>
      {isOpen &&
        <MenuItemContainer style={{ transform: `translateX(calc(-50% + ${buttonWidth / 2}px))` }}>
          {children}
        </MenuItemContainer>
      }
    </MenuButtonWrapper>
  );
};

const MenuItem = Styled.button`
  align-items: center;
  background-color: ${Theme.blue};
  border: none;
  box-sizing: border-box;
  color: white;
  display: flex;
  font-family: ${Theme.fontFamily};
  font-size: ${Theme.fontSizeText};
  width: 100%;
  padding: 8px 15px 8px 15px;
  text-align: center;
  transition: background-color .10s, opacity .25s;

  :not([disabled]):hover {
    cursor: pointer;
    background-color: ${lighten(0.1, Theme.blue)};
  }

  :disabled:not(.is-pro) {
    opacity: 0.35;
  }

  :focus {
    outline: none;
  }

  &.is-pro {
    background-color: ${Theme.proGray};
    justify-content: space-between;
  }
`;

const StyledNekoMenuItem = props => {

  const { onClick, children, isPro, requirePro = false, disabled } = props;
  const needsProUser = requirePro && !isPro;

  return (
    <MenuItem type="button" className={needsProUser? 'is-pro' : ''} onClick={onClick} disabled={disabled || needsProUser}>
      <span>{children}</span>
      {needsProUser && !isPro && <ProOnly />}
    </MenuItem>
  );
};

/**
 * The NekoMenuButton works out of the box without specific attributes.
 */
const NekoMenuButton = (props) => {
  return (<StyledMenuButton {...props} />);
}
const NekoMenuItem = (props) => {
  return (<StyledNekoMenuItem {...props} />);
}

NekoMenuButton.propTypes = {
  title: PropTypes.string,
  disabled: PropTypes.bool,
  isPro: PropTypes.bool
};

NekoMenuButton.defaultProps = {
  title: undefined,
  disabled: false,
  isPro: false
};

NekoMenuItem.propTypes = {
  onClick: PropTypes.func.isRequired,
  requirePro: PropTypes.bool
};

NekoMenuItem.defaultProps = {
  onClick: () => {},
  requirePro: false
};

export { NekoMenuButton, NekoMenuItem };
